import { useCallback, useMemo, useRef, useState } from 'react';

import { useRovingTabIndex } from '@/hooks/useRovingTabIndex';

import { useScrollTabsObserver } from './useScrollTabsObserver';
import { useScrollToSection } from './useScrollToSection';
import { type ScrollTabsPanelProps } from '../types/types';

type UseScrollTabsPanelOptions<T> = Pick<
  ScrollTabsPanelProps<T>,
  'items' | 'contentTitle' | 'defaultActiveId' | 'onTabChange'
>;

type UseScrollTabsPanelReturn = {
  activeId: string | number;
  slug: string;
  firstItemId: string | number | undefined;
  lastItemId: string | number | undefined;
  register: (id: string | number, el: HTMLElement | null) => void;
  gotoItem: (id: string | number, forceFocus?: boolean) => void;
  prevItem: () => void;
  nextItem: () => void;
  scrollableContainerRef: React.RefObject<HTMLElement | null>;
  tabsContainerRef: React.RefObject<HTMLDivElement | null>;
};

export function useScrollTabsPanel<T = unknown>({
  items,
  contentTitle,
  defaultActiveId,
  onTabChange,
}: UseScrollTabsPanelOptions<T>): UseScrollTabsPanelReturn {
  const [activeId, setActiveId] = useState<string | number>(() => {
    if (defaultActiveId !== undefined) return defaultActiveId;
    if (items.length === 0) return '';
    return items[0]?.id ?? '';
  });

  const scrollableContainerRef = useRef<HTMLElement>(null);
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  const slug = contentTitle.toLowerCase().replace(/\s+/g, '-');

  const firstItemId = items.length > 0 ? items[0]?.id : undefined;
  const lastItemId = items.length > 0 ? items[items.length - 1]?.id : undefined;
  const activeIndex = useMemo(
    () => (items.length > 0 ? items.findIndex((item) => item.id === activeId) : -1),
    [items, activeId]
  );

  const { register, sectionRefs, isProgrammaticScroll } = useScrollTabsObserver({
    items,
    containerRef: scrollableContainerRef,
    onChange: (id) => {
      if (id !== activeId) {
        setActiveId(id);
        onTabChange?.(id);
      }
    },
    dataAttribute: 'data-item-id',
    getElementId: (id) => `${slug}_${id}`,
    axis: 'y',
  });

  const { scrollToId } = useScrollToSection({
    containerRef: scrollableContainerRef,
    sectionRefs,
    isProgrammaticScroll,
    scrollOffset: 0,
    axis: 'y',
  });

  useRovingTabIndex(tabsContainerRef);

  const scrollTabIntoView = useCallback((id: string | number, forceFocus?: boolean): void => {
    const container = tabsContainerRef.current;
    if (!container) return;
    const button = container.querySelector<HTMLElement>(`[data-tab-id="${id}"]`);
    if (!button) return;
    if (forceFocus) button.focus();
    button.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, []);

  const gotoItem = useCallback(
    (id: string | number, forceFocus?: boolean): void => {
      const item = items.find((item) => item.id === id);
      if (!item) return;
      setActiveId(id);
      onTabChange?.(id);
      scrollTabIntoView(id, forceFocus);
      scrollToId(id);
    },
    [items, onTabChange, scrollTabIntoView, scrollToId]
  );

  const prevItem = useCallback((): void => {
    if (items.length === 0 || activeIndex <= 0) return;
    const prev = items[activeIndex - 1];
    if (prev !== undefined) gotoItem(prev.id, true);
  }, [activeIndex, items, gotoItem]);

  const nextItem = useCallback((): void => {
    if (items.length === 0 || activeIndex < 0 || activeIndex >= items.length - 1) return;
    const next = items[activeIndex + 1];
    if (next !== undefined) gotoItem(next.id, true);
  }, [activeIndex, items, gotoItem]);

  return {
    activeId,
    slug,
    firstItemId,
    lastItemId,
    register,
    gotoItem,
    prevItem,
    nextItem,
    scrollableContainerRef,
    tabsContainerRef,
  };
}
